import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/api/admin';
import { CasinoButton } from '@/components/CasinoButton';
import { AdminHeader } from './TournamentNew';

/**
 * Admin CSV export page.
 *
 * Per-tournament: dropdown defaults to the active tournament if any,
 * else the most recent. Overall: a single button hits the all-time
 * endpoint. Both endpoints are admin-gated server-side.
 *
 * Download mechanism: same-origin anchor navigation via
 * `window.location.assign`. The browser sends the session cookie on the
 * GET and honors the server's Content-Disposition filename. The
 * fetch-then-createObjectURL alternative loses the filename unless you
 * parse the Content-Disposition header by hand, and buffers the whole
 * file into memory — anchor nav avoids both.
 */
export function AdminExport() {
  const q = useQuery({
    queryKey: ['admin', 'tournaments'],
    queryFn: adminApi.listTournaments,
  });

  const tournaments = q.data?.tournaments ?? [];

  // Stable ordering: newest first by createdAt. listTournaments already
  // orders descending server-side, but resort defensively in case that
  // ever changes.
  const sorted = useMemo(
    () =>
      [...tournaments].sort((a, b) =>
        a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0,
      ),
    [tournaments],
  );

  // Default selection: active tournament if present, else the newest.
  const defaultId = useMemo(() => {
    const active = sorted.find((t) => t.status === 'active');
    return (active ?? sorted[0])?.id ?? null;
  }, [sorted]);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const effectiveId = selectedId ?? defaultId;
  const selected = sorted.find((t) => t.id === effectiveId) ?? null;

  // Both buttons share the same brief "Preparing…" affordance — purely
  // to swallow accidental double-clicks while the browser is starting
  // the download. We can't observe completion (anchor nav doesn't
  // surface a callback), so a fixed-duration unlock is good enough.
  const [pending, setPending] = useState<'per' | 'overall' | null>(null);

  function triggerDownload(url: string, kind: 'per' | 'overall') {
    if (pending) return;
    setPending(kind);
    window.location.assign(url);
    // Re-enable shortly after — the navigation itself doesn't navigate
    // away (the response is an attachment), so the page stays mounted.
    window.setTimeout(() => setPending(null), 1500);
  }

  const downloadPerTournament = () => {
    if (!effectiveId) return;
    triggerDownload(
      `/api/admin/tournaments/${effectiveId}/export.csv`,
      'per',
    );
  };

  const downloadOverall = () =>
    triggerDownload('/api/admin/export.csv', 'overall');

  return (
    <main className="mx-auto max-w-3xl px-5 py-8 space-y-6">
      <AdminHeader
        title="Export"
        crumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Export' }]}
      />

      <section className="cabinet-card p-5 space-y-4">
        <header>
          <h2 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
            Per-tournament CSV
          </h2>
          <p className="text-sm text-cabinet-cream/70 mt-1">
            One row per non-tiebreaker entry, including ranks, payouts, and
            paid status. Opens in Excel / Sheets directly.
          </p>
        </header>

        {q.isLoading ? (
          <p className="text-sm text-cabinet-cream/60">Loading tournaments…</p>
        ) : sorted.length === 0 ? (
          <p className="text-sm text-cabinet-cream/60">
            No tournaments yet. Create one from{' '}
            <span className="text-cabinet-gold">Admin → New tournament</span>.
          </p>
        ) : (
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
              Tournament
            </span>
            <select
              value={effectiveId ?? ''}
              onChange={(e) => setSelectedId(Number(e.target.value))}
              className="w-full rounded-md bg-black/50 border-2 border-cabinet-cream/20 px-3 py-2.5 text-cabinet-cream focus:border-cabinet-gold focus:outline-none focus:ring-2 focus:ring-cabinet-gold/40 transition"
            >
              {sorted.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} — {t.status}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="pt-1">
          <CasinoButton
            variant="gold"
            onClick={downloadPerTournament}
            disabled={!selected || pending !== null}
          >
            {pending === 'per' ? 'Preparing…' : 'Download CSV'}
          </CasinoButton>
        </div>
      </section>

      <section className="cabinet-card p-5 space-y-4">
        <header>
          <h2 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
            All-time CSV
          </h2>
          <p className="text-sm text-cabinet-cream/70 mt-1">
            One row per player across every tournament: entries, spend,
            winnings, net, wins, and best finish.
          </p>
        </header>
        <div>
          <CasinoButton
            variant="gold"
            onClick={downloadOverall}
            disabled={pending !== null}
          >
            {pending === 'overall' ? 'Preparing…' : 'Download all-time CSV'}
          </CasinoButton>
        </div>
      </section>
    </main>
  );
}
